import React from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { classNames }  from '../lib/classNames';

const types = {
  success: {
    icon: CheckCircleIcon,
    color: {
      icon: `text-paidniceGreen-600`,
      bg: `bg-paidniceGreen-50`,
      heading: `text-paidniceGreen-900`,
      content: `text-paidniceGreen-800`,
      border: `border-paidniceGreen-200`,
    },
  },
  successGreen: {
    icon: CheckCircleIcon,
    color: {
      icon: `text-green-600`,
      bg: `bg-green-50`,
      heading: `text-green-900`,
      content: `text-green-800`,
      border: `border-green-200`,
    },
  },
  warning: {
    icon: ExclamationTriangleIcon,
    color: {
      icon: `text-orange-700`,
      bg: `bg-orange-50`,
      heading: `text-orange-900`,
      content: `text-orange-800`,
      border: `border-orange-200`,
    },
  },
  error: {
    icon: XCircleIcon,
    color: {
      icon: `text-red-600`,
      bg: `bg-red-50`,
      heading: `text-red-900`,
      content: `text-red-800`,
      border: `border-red-200`,
    },
  },
  info: {
    icon: InformationCircleIcon,
    color: {
      icon: `text-slate-600`,
      bg: `bg-slate-50`,
      heading: `text-slate-900`,
      content: `text-slate-700`,
      border: `border-slate-200`,
    },
  },
  pro: {
    icon: null,
    color: {
      icon: `text-purple-600`,
      bg: `bg-purple-50`,
      heading: `text-purple-900`,
      content: `text-purple-800`,
      border: `border-purple-200`,
    },
  },
};

interface INotificationProps {
  type?: `success` | `warning` | `error` | `info` | `pro` | `successGreen` | string;
  title?: string;
  buttons?: React.ReactNode;
  children?: React.ReactNode;
  icon?: typeof ExclamationTriangleIcon; // Use any heroicons for the type
  className?: string;
  noIcon?: boolean;
}

export function Notification({
  type = `info`,
  title,
  buttons,
  children,
  icon,
  className,
  noIcon,
}: INotificationProps) {
  const {
    color,
    icon: Icon,
  } = types[type];

  const IconOverride = icon || Icon;

  return (
    <div className={
      classNames(
        `flex items-center gap-3 rounded-lg border px-4 py-3`,
        color.bg,
        color.border,
        className,
      )
    }>
      { IconOverride && !noIcon ? (
        <IconOverride className={ `h-5 w-5 shrink-0 ${color.icon}` }
          aria-hidden={ `true` } />
      ) : null }
      <div className={ `min-w-0 flex-1 text-sm ${color.content}` }>
        { title ? (
          <h3 className={ `font-semibold ${color.heading}` }>{ title }</h3>
        ) : null }
        { children }
      </div>
      { buttons ? (
        <div className={ `ml-4 flex shrink-0 items-center gap-2` }>
          { buttons }
        </div>
      ) : null }
    </div>
  );
}
